import { apiClient, API_BASE_URL } from '@/lib/api-client';
import { tokenStorage } from '@/utils/token-storage';
import type { ResolvedEstimate } from '@/types/estimate';

/**
 * Founder bug 7 diagnosis aid (2026-07-31) — a failed PDF/preview fetch
 * previously alerted the raw response body, which for a proxy/EB error
 * page is a wall of HTML. Reduce to a status + a short readable extract
 * (JSON `message` when present, tags stripped otherwise).
 */
async function pdfErrorMessage(res: Response, doing: string): Promise<string> {
  const raw = await res.text().catch(() => '');
  let detail = raw;
  try {
    const parsed = JSON.parse(raw) as { message?: string | string[] };
    if (parsed && parsed.message) {
      detail = Array.isArray(parsed.message)
        ? parsed.message.join('; ')
        : parsed.message;
    }
  } catch {
    detail = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  if (detail.length > 240) detail = `${detail.slice(0, 240)}…`;
  return `Could not ${doing} (HTTP ${res.status}).${detail ? ` Server said: ${detail}` : ''}`;
}

export interface UpdateLineItemPayload {
  description?: string;
  quantity?: string;
  unit?: string;
  customUnit?: string | null;
  purpose?: string | null;
  isAllowance?: boolean;
  position?: number;
}

export interface CreateLineItemPayload {
  type: 'MATERIAL' | 'SERVICE' | 'OTHER';
  /** Attaches the line item to a specific CSF row. Null/omitted = Overhead item. */
  projectScopeElementFieldId?: string | null;
  description: string;
  quantity: string;
  unit: string;
  customUnit?: string | null;
  purpose?: string | null;
  isAllowance?: boolean;
  initialCost?: {
    productMsku?: string | null;
    serviceId?: string | null;
    description: string;
    unitCostSnapshot: string;
  };
}

/**
 * Estimate drawer (founder 2026-07-30, Option 4c) — atomic product
 * re-bind from the drawer's MPT search. Mirrors BindLineItemDto:
 * the backend updates the line item's live columns and replaces ALL
 * its cost rows with the picked cost in one transaction. `unitCost`
 * null = the catalog row carries no cost; the bound cost row stays
 * UNRESOLVED (never fabricated) and keeps blocking finalize.
 */
export interface BindLineItemPayload {
  type: 'MATERIAL' | 'SERVICE';
  sku: string;
  description: string;
  unit: string;
  unitCost?: string | null;
}

/**
 * Sprint 41 W22 / W29 — Section VI discount-line CRUD payload + response.
 *
 * appliesTo=PROJECT subtracts once from grandTotal. appliesTo=ELEMENT
 * requires projectScopeElementId and only subtracts when that element
 * is included (clientIncluded=true).
 */
export interface CreateDiscountPayload {
  appliesTo: 'PROJECT' | 'ELEMENT';
  description: string;
  amount: string;
  projectScopeElementId?: string | null;
}

export interface UpdateDiscountPayload {
  appliesTo?: 'PROJECT' | 'ELEMENT';
  description?: string;
  amount?: string;
  projectScopeElementId?: string | null;
}

export interface OverrideCostPayload {
  unitCostOverride: string | null;
  overrideReason?: string | null;
}

/**
 * Sprint 41 W21 / W27 — shape returned by
 * GET /estimate/line-items/:id/cost-history.
 */
export interface CostHistoryEntry {
  id: string;
  projectId: string;
  projectName?: string | null;
  projectNumber?: string | null;
  description: string;
  unitCostSnapshot: string;
  unitCostOverride?: string | null;
  costSource: string;
  sourceLayerSnapshot?: {
    invoice?: number;
    partner?: number;
    default?: number;
    labor?: number;
  } | null;
  snapshotTakenAt: string;
}

export interface CostHistoryGroup {
  costId: string;
  productMsku?: string | null;
  serviceId?: string | null;
  partnerServiceId?: string | null;
  laborCategoryId?: string | null;
  history: CostHistoryEntry[];
}

export interface LineItemCostHistory {
  lineItemId: string;
  groups: CostHistoryGroup[];
}

export const estimateApi = {
  /**
   * Resolves a Project into a fully-hydrated Estimate payload —
   * project + client + scope elements + line items + costs +
   * section templates + investment summary — in one round-trip.
   */
  resolve: async (
    projectId: string,
    changeOrderId?: string | null,
  ): Promise<ResolvedEstimate> => {
    const qs = changeOrderId
      ? `?changeOrderId=${encodeURIComponent(changeOrderId)}`
      : '';
    return apiClient.get<ResolvedEstimate>(
      `/estimate/${encodeURIComponent(projectId)}${qs}`,
    );
  },

  /**
   * Learning feed — seed vs current estimator BOM deltas for a project.
   * Powers validation / improvement of calc logic from estimator edits.
   */
  getSeedLearningDeltas: async (
    projectId: string,
  ): Promise<{
    projectId: string;
    totalSeeded: number;
    changedCount: number;
    deltas: Array<{
      lineItemId: string;
      purpose: string | null;
      seedPurpose: string | null;
      description: string;
      seedDescription: string | null;
      quantity: string;
      seedQuantity: string | null;
      seedSource: string | null;
    }>;
  }> => {
    return apiClient.get(
      `/estimate/${encodeURIComponent(projectId)}/learning/seed-deltas`,
    );
  },

  createLineItem: async (
    elementId: string,
    payload: CreateLineItemPayload,
  ): Promise<{ id: string }> => {
    return apiClient.post<{ id: string }>(
      `/estimate/elements/${encodeURIComponent(elementId)}/line-items`,
      payload,
    );
  },

  /**
   * Create a line item attached to a Section II ProjectDetail row.
   * Parallel to createLineItem but the parent is a ProjectDetail, not
   * a ProjectScopeElement.
   */
  createDetailLineItem: async (
    detailId: string,
    payload: Omit<CreateLineItemPayload, 'projectScopeElementFieldId'>,
  ): Promise<{ id: string }> => {
    return apiClient.post<{ id: string }>(
      `/estimate/details/${encodeURIComponent(detailId)}/line-items`,
      payload,
    );
  },

  /**
   * Founder requirement 2026-07 — inline-edit a scope-element CSF
   * selection. Value-only update; the backend validates the value
   * against the linked intake question's ACTIVE version (422 on an
   * invalid option code) and rejects once the estimate is finalized.
   */
  updateFieldValue: async (
    fieldId: string,
    value: unknown,
  ): Promise<{ id: string }> => {
    return apiClient.patch<{ id: string }>(
      `/estimate/fields/${encodeURIComponent(fieldId)}/value`,
      { value },
    );
  },

  updateLineItem: async (
    id: string,
    payload: UpdateLineItemPayload,
  ): Promise<{ ok: true }> => {
    return apiClient.patch<{ ok: true }>(
      `/estimate/line-items/${encodeURIComponent(id)}`,
      payload,
    );
  },

  deleteLineItem: async (id: string): Promise<{ ok: true }> => {
    return apiClient.delete<{ ok: true }>(
      `/estimate/line-items/${encodeURIComponent(id)}`,
    );
  },

  /** Estimate drawer — PATCH /estimate/line-items/:id/bind (atomic re-bind). */
  bindLineItem: async (
    id: string,
    payload: BindLineItemPayload,
  ): Promise<{ ok: true }> => {
    return apiClient.patch<{ ok: true }>(
      `/estimate/line-items/${encodeURIComponent(id)}/bind`,
      payload,
    );
  },

  overrideCost: async (
    id: string,
    payload: OverrideCostPayload,
  ): Promise<{ ok: true }> => {
    return apiClient.patch<{ ok: true }>(
      `/estimate/costs/${encodeURIComponent(id)}`,
      payload,
    );
  },

  updateElementMargin: async (
    id: string,
    marginPercentage: string,
  ): Promise<{ ok: true }> => {
    return apiClient.patch<{ ok: true }>(
      `/estimate/elements/${encodeURIComponent(id)}/margin`,
      { marginPercentage },
    );
  },

  toggleElementInclusion: async (
    id: string,
    clientIncluded: boolean,
  ): Promise<{ ok: true }> => {
    return apiClient.patch<{ ok: true }>(
      `/estimate/elements/${encodeURIComponent(id)}/inclusion`,
      { clientIncluded },
    );
  },

  addCostRow: async (
    lineItemId: string,
    payload: {
      productMsku?: string | null;
      serviceId?: string | null;
      description: string;
      unitCostSnapshot: string;
    },
  ): Promise<{ id: string }> => {
    return apiClient.post<{ id: string }>(
      `/estimate/line-items/${encodeURIComponent(lineItemId)}/costs`,
      payload,
    );
  },

  deleteCostRow: async (id: string): Promise<{ ok: true }> => {
    return apiClient.delete<{ ok: true }>(
      `/estimate/costs/${encodeURIComponent(id)}`,
    );
  },

  /**
   * Sprint 41 W22 / W29 — Section VI discount-line CRUD. PROJECT-scope
   * discounts subtract once from grandTotal. ELEMENT-scope discounts
   * require projectScopeElementId and only apply when the target
   * element is included. All mutations rejected once project status =
   * ESTIMATE_ACCEPTED.
   */
  createDiscount: async (
    projectId: string,
    payload: CreateDiscountPayload,
  ): Promise<{ id: string }> => {
    return apiClient.post<{ id: string }>(
      `/estimate/${encodeURIComponent(projectId)}/discounts`,
      payload,
    );
  },

  updateDiscount: async (
    id: string,
    payload: UpdateDiscountPayload,
  ): Promise<{ ok: true }> => {
    return apiClient.patch<{ ok: true }>(
      `/estimate/discounts/${encodeURIComponent(id)}`,
      payload,
    );
  },

  deleteDiscount: async (id: string): Promise<{ ok: true }> => {
    return apiClient.delete<{ ok: true }>(
      `/estimate/discounts/${encodeURIComponent(id)}`,
    );
  },

  /**
   * Sprint 41 W21 / W27 — historical resolutions per cost row on a
   * line item, scoped to the line item's organization. Powers the
   * Section III cost-history side panel.
   */
  getLineItemCostHistory: async (
    lineItemId: string,
  ): Promise<LineItemCostHistory> => {
    return apiClient.get<LineItemCostHistory>(
      `/estimate/line-items/${encodeURIComponent(lineItemId)}/cost-history`,
    );
  },

  finalize: async (
    projectId: string,
  ): Promise<{
    lockedElements: number;
    lockedLineItems: number;
    contractId: string | null;
    contractCreateError: string | null;
  }> => {
    return apiClient.post<{
      lockedElements: number;
      lockedLineItems: number;
      contractId: string | null;
      contractCreateError: string | null;
    }>(
      `/estimate/${encodeURIComponent(projectId)}/finalize`,
      {},
    );
  },

  /**
   * Change-order document lifecycle — finalize a CO-scoped estimate
   * (ESTIMATE.MODIFY). 422 codes ESTIMATE_HAS_UNRESOLVED_COSTS /
   * ESTIMATE_NEEDS_STATEMENT; 400 when the CO has no scope elements.
   */
  finalizeChangeOrder: async (
    projectId: string,
    changeOrderId: string,
  ): Promise<{ lockedElements: number; lockedLineItems: number; snapshotId: string }> => {
    return apiClient.post<{
      lockedElements: number;
      lockedLineItems: number;
      snapshotId: string;
    }>(
      `/estimate/${encodeURIComponent(projectId)}/change-orders/${encodeURIComponent(changeOrderId)}/finalize`,
      {},
    );
  },

  reopen: async (
    projectId: string,
  ): Promise<{ unlockedElements: number; unlockedLineItems: number }> => {
    return apiClient.post<{ unlockedElements: number; unlockedLineItems: number }>(
      `/estimate/${encodeURIComponent(projectId)}/reopen`,
      {},
    );
  },

  acknowledge: async (
    projectId: string,
    role: 'CONTRACTOR' | 'CLIENT',
  ): Promise<{
    contractorAcknowledgedAt: string | null;
    clientAcknowledgedAt: string | null;
    autoFinalized: boolean;
  }> => {
    return apiClient.post(
      `/estimate/${encodeURIComponent(projectId)}/acknowledge`,
      { role },
    );
  },

  unacknowledge: async (
    projectId: string,
    role: 'CONTRACTOR' | 'CLIENT',
  ): Promise<{
    contractorAcknowledgedAt: string | null;
    clientAcknowledgedAt: string | null;
    autoReopened: boolean;
  }> => {
    return apiClient.post(
      `/estimate/${encodeURIComponent(projectId)}/unacknowledge`,
      { role },
    );
  },

  markDelivered: async (
    projectId: string,
  ): Promise<{ deliveredAt: string; deliveredBy: string | null }> => {
    return apiClient.post<{ deliveredAt: string; deliveredBy: string | null }>(
      `/estimate/${encodeURIComponent(projectId)}/deliver`,
      {},
    );
  },

  /**
   * Bug 7 — the estimator returns a submitted/in-process estimate to
   * the sales rep with a required reason. The intake form unlocks for
   * the rep; resubmission re-seeds revised elements.
   */
  returnForRevision: async (
    projectId: string,
    reason: string,
  ): Promise<{ projectId: string; status: string; reason: string }> => {
    return apiClient.post<{ projectId: string; status: string; reason: string }>(
      `/estimate/${encodeURIComponent(projectId)}/return`,
      { reason },
    );
  },

  /**
   * Fetches the PDF as a Blob so callers can trigger a download.
   * Bypasses `apiClient.get` because that parses the response as JSON.
   */
  downloadPdf: async (projectId: string): Promise<Blob> => {
    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/estimate/${encodeURIComponent(projectId)}/pdf`;
    const res = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error(await pdfErrorMessage(res, 'download the PDF'));
    }
    return res.blob();
  },

  /**
   * Preview mode — fetches rendered HTML (same pipeline, no Puppeteer)
   * and returns a Blob so the caller can open it in a new tab. Much
   * faster round-trip than generating a full PDF; useful for template
   * authoring + sales-rep pre-flight.
   */
  updateContract: async (
    projectId: string,
    payload: {
      projectName?: string | null;
      summary?: string | null;
      highlights?: string | null;
      estimatedStartDate?: string | null;
      estimatedDurationDays?: number | null;
      weeksUntilStart?: number | null;
      weeksToComplete?: number | null;
      tentativeStartDate?: string | null;
      assumptionsOverride?: string | null;
      /** Sprint 41 W23 / W28 — Section IV rich-text override. Null clears. */
      contractTermsOverride?: string | null;
      /** Section V warranty snapshot. Null clears to Document Language. */
      limitedLiabilityWarrantyOverride?: string | null;
      paymentMilestones?: Array<{
        ref: number;
        payment: string;
        milestone: string;
        pct: number;
      }> | null;
      /** Sprint 41 W32 enrichment Slice 5 — Section VI allowances list. Null clears. */
      allowances?: Array<{
        type: string;
        description: string;
        amount: string | number;
      }> | null;
      /** Task 18 — Project Details row margin % for TCS. */
      projectDetailsMarginPercentage?: string;
      /** Tenant Location id for Section I contractor block (explicit Edit). */
      contractorLocationId?: string | null;
    },
  ): Promise<void> => {
    await apiClient.patch(`/estimate/${encodeURIComponent(projectId)}/contract`, payload);
  },

  generateSummary: async (projectId: string): Promise<{ summary: string }> => {
    return apiClient.post<{ summary: string }>(
      `/estimate/${encodeURIComponent(projectId)}/generate-summary`,
      {},
    );
  },

  previewHtml: async (projectId: string): Promise<Blob> => {
    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/estimate/${encodeURIComponent(projectId)}/pdf?format=html`;
    const res = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new Error(await pdfErrorMessage(res, 'load the preview'));
    }
    return res.blob();
  },
};
